// ============================================================
// NACHA ACH FILE GENERATOR
// Standard Entry Class: PPD (Prearranged Payment & Deposit)
// Format: Fixed-width 94-character records
// Spec: NACHA Operating Rules & Guidelines
// ============================================================

export interface NACHACompanyInfo {
  companyName: string        // Your company name (max 16 chars)
  companyId: string          // 10-digit company ID (IRS EIN recommended)
  originatingDfiId: string   // Your bank routing number first 8 digits
  fileIdModifier?: string    // A-Z, default "A"
}

export interface NACHAEntry {
  routingNumber: string      // 9-digit bank routing number
  accountNumber: string      // Bank account number (max 17 chars)
  accountType: "checking" | "savings"
  amount: number             // Amount in CENTS (integer)
  receiverName: string       // Receiver name (max 22 chars)
  traceId: string            // Unique trace identifier (max 15 chars)
  payoutId: string           // Reference for audit
}

export interface NACHABatch {
  entries: NACHAEntry[]
  effectiveDate: string      // YYMMDD format
  description: string        // Max 10 chars
}

// ── Padding helpers ───────────────────────────────────────────

function padRight(str: string, len: number, char = " "): string {
  return str.slice(0, len).padEnd(len, char)
}

function padLeft(str: string, len: number, char = "0"): string {
  return str.slice(0, len).padStart(len, char)
}

function formatDate(): string {
  const d = new Date()
  const yy = String(d.getFullYear()).slice(2)
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return yy + mm + dd
}

function formatTime(): string {
  const d = new Date()
  const hh = String(d.getHours()).padStart(2, "0")
  const mi = String(d.getMinutes()).padStart(2, "0")
  return hh + mi
}

// ── Routing number checksum validation ────────────────────────

export function validateRoutingNumber(routing: string): boolean {
  if (!/^\d{9}$/.test(routing)) return false
  const d = routing.split("").map(Number)
  const checksum =
    3 * (d[0] + d[3] + d[6]) +
    7 * (d[1] + d[4] + d[7]) +
    1 * (d[2] + d[5] + d[8])
  return checksum % 10 === 0
}

// ── Banking day validation ────────────────────────────────────
// Returns true if the date is a valid ACH effective date
// ACH cannot settle on weekends or US federal holidays

const US_FEDERAL_HOLIDAYS_2025_2026 = [
  "20250101","20250120","20250217","20250528","20250619",
  "20250704","20250901","20251013","20251111","20251127","20251225",
  "20260101","20260119","20260216","20260525","20260619",
  "20260703","20260907","20261012","20261111","20261126","20261225",
]

export function isValidACHDate(dateStr: string): boolean {
  // dateStr format: YYYYMMDD
  const date = new Date(
    parseInt(dateStr.slice(0, 4)),
    parseInt(dateStr.slice(4, 6)) - 1,
    parseInt(dateStr.slice(6, 8))
  )
  const dow = date.getDay()
  if (dow === 0 || dow === 6) return false // weekend
  if (US_FEDERAL_HOLIDAYS_2025_2026.includes(dateStr)) return false
  return true
}

export function nextValidACHDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1) // ACH is next-day minimum
  for (let i = 0; i < 10; i++) {
    const str = d.toISOString().slice(0, 10).replace(/-/g, "")
    if (isValidACHDate(str)) return str
    d.setDate(d.getDate() + 1)
  }
  return d.toISOString().slice(0, 10).replace(/-/g, "")
}

// ── Transaction code ──────────────────────────────────────────

function transactionCode(accountType: "checking" | "savings"): string {
  // 22 = checking credit, 32 = savings credit
  return accountType === "checking" ? "22" : "32"
}

// ── Routing number hash ───────────────────────────────────────

function routingHash(entries: NACHAEntry[]): string {
  const sum = entries.reduce((acc, e) => {
    return acc + parseInt(e.routingNumber.slice(0, 8))
  }, 0)
  // Take last 10 digits
  return String(sum).slice(-10).padStart(10, "0")
}

// ── Record builders ───────────────────────────────────────────

function buildFileHeader(company: NACHACompanyInfo): string {
  // Record type 1 — 94 chars
  return [
    "1",                                           // Record type code
    "01",                                          // Priority code
    " " + padLeft(company.originatingDfiId, 9),   // Immediate destination (space + 9 digits)
    padLeft(company.companyId, 10),                // Immediate origin
    formatDate(),                                  // File creation date YYMMDD
    formatTime(),                                  // File creation time HHMM
    company.fileIdModifier ?? "A",                 // File ID modifier
    "094",                                         // Record size
    "10",                                          // Blocking factor
    "1",                                           // Format code
    padRight("ZIMRAH HEALTH", 23),                 // Immediate destination name
    padRight("ZIMRAH HEALTH", 23),                 // Immediate origin name
    "        ",                                    // Reference code (8 spaces)
  ].join("")
}

function buildBatchHeader(
  company: NACHACompanyInfo,
  batch: NACHABatch,
  batchNumber: number
): string {
  // Record type 5 — 94 chars
  const effectiveDateFormatted = batch.effectiveDate.slice(2) // YYMMDD from YYYYMMDD
  return [
    "5",                                           // Record type code
    "200",                                         // Service class code (200 = mixed, 220 = credits only)
    padRight(company.companyName, 16),             // Company name
    padRight("", 20),                              // Company discretionary data
    padLeft(company.companyId, 10),                // Company ID
    "PPD",                                         // Standard entry class code
    padRight(batch.description, 10),               // Company entry description
    "      ",                                      // Company descriptive date (6 spaces)
    effectiveDateFormatted,                        // Effective entry date YYMMDD
    "   ",                                         // Settlement date (filled by bank)
    "1",                                           // Originator status code
    padLeft(company.originatingDfiId, 8),          // Originating DFI ID (8 digits)
    padLeft(String(batchNumber), 7),               // Batch number
  ].join("")
}

function buildEntryDetail(
  entry: NACHAEntry,
  originatingDfiId: string,
  sequenceNumber: number
): string {
  // Record type 6 — 94 chars
  const routing8 = entry.routingNumber.slice(0, 8)
  const checkDigit = entry.routingNumber.slice(8, 9)
  const amountStr = padLeft(String(Math.round(entry.amount)), 10) // in cents, no decimal

  return [
    "6",                                           // Record type code
    transactionCode(entry.accountType),            // Transaction code
    routing8,                                      // Receiving DFI routing (8 digits)
    checkDigit,                                    // Check digit
    padRight(entry.accountNumber, 17),             // DFI account number
    amountStr,                                     // Amount (10 digits, cents)
    padRight(entry.traceId, 15),                   // Individual ID number
    padRight(entry.receiverName, 22),              // Individual name
    "  ",                                          // Discretionary data
    "0",                                           // Addenda record indicator
    padLeft(originatingDfiId, 8) + padLeft(String(sequenceNumber), 7), // Trace number
  ].join("")
}

function buildBatchControl(
  company: NACHACompanyInfo,
  batch: NACHABatch,
  batchNumber: number,
  entryCount: number,
  totalAmount: number
): string {
  // Record type 8 — 94 chars
  return [
    "8",                                           // Record type code
    "200",                                         // Service class code
    padLeft(String(entryCount), 6),                // Entry/addenda count
    routingHash(batch.entries),                    // Entry hash
    padLeft(String(Math.round(totalAmount)), 12),  // Total debit amount (0 for credits only)
    padLeft(String(Math.round(totalAmount)), 12),  // Total credit amount
    padLeft(company.companyId, 10),                // Company ID
    padRight("", 19),                              // Message authentication code
    "   ",                                         // Reserved
    padLeft(company.originatingDfiId, 8),          // Originating DFI ID
    padLeft(String(batchNumber), 7),               // Batch number
  ].join("")
}

function buildFileControl(
  batchCount: number,
  blockCount: number,
  entryCount: number,
  entries: NACHAEntry[],
  totalAmount: number
): string {
  // Record type 9 — 94 chars
  const allHash = entries.reduce((acc, e) => acc + parseInt(e.routingNumber.slice(0, 8)), 0)
  const hashStr = String(allHash).slice(-10).padStart(10, "0")

  return [
    "9",                                           // Record type code
    padLeft(String(batchCount), 6),                // Batch count
    padLeft(String(blockCount), 6),                // Block count (multiples of 10)
    padLeft(String(entryCount), 8),                // Entry/addenda count
    hashStr,                                       // Entry hash
    padLeft(String(Math.round(totalAmount)), 12),  // Total debit amount
    padLeft(String(Math.round(totalAmount)), 12),  // Total credit amount
    padRight("", 39),                              // Reserved
  ].join("")
}

// ── Main generator ────────────────────────────────────────────

export interface NACHAResult {
  file: string
  lineCount: number
  entryCount: number
  totalAmountCents: number
  batchCount: number
  errors: string[]
}

export function generateNACHAFile(
  company: NACHACompanyInfo,
  batch: NACHABatch
): NACHAResult {
  const errors: string[] = []

  // Validate entries
  batch.entries.forEach((entry, i) => {
    if (!validateRoutingNumber(entry.routingNumber)) {
      errors.push(`Entry ${i + 1}: Invalid routing number ${entry.routingNumber}`)
    }
    if (entry.amount <= 0) {
      errors.push(`Entry ${i + 1}: Amount must be greater than 0`)
    }
    if (!entry.accountNumber.trim()) {
      errors.push(`Entry ${i + 1}: Account number required`)
    }
  })

  if (errors.length > 0) {
    return { file: "", lineCount: 0, entryCount: 0, totalAmountCents: 0, batchCount: 0, errors }
  }

  const lines: string[] = []
  const batchNumber = 1
  let sequenceNumber = 1
  const totalAmountCents = batch.entries.reduce((sum, e) => sum + Math.round(e.amount), 0)

  // File header
  lines.push(buildFileHeader(company))

  // Batch header
  lines.push(buildBatchHeader(company, batch, batchNumber))

  // Entry detail records
  for (const entry of batch.entries) {
    lines.push(buildEntryDetail(entry, company.originatingDfiId, sequenceNumber++))
  }

  // Batch control
  lines.push(buildBatchControl(company, batch, batchNumber, batch.entries.length, totalAmountCents))

  // File control
  const totalRecords = lines.length + 1 // +1 for file control
  const blockCount = Math.ceil((totalRecords + 1) / 10) // round up to nearest 10

  lines.push(buildFileControl(1, blockCount, batch.entries.length, batch.entries, totalAmountCents))

  // Pad to multiple of 10 with "9" records
  while (lines.length % 10 !== 0) {
    lines.push("9".repeat(94))
  }

  // Validate all lines are exactly 94 chars
  lines.forEach((line, i) => {
    if (line.length !== 94) {
      errors.push(`Line ${i + 1} is ${line.length} chars, expected 94`)
    }
  })

  return {
    file: lines.join("\n"),
    lineCount: lines.length,
    entryCount: batch.entries.length,
    totalAmountCents,
    batchCount: 1,
    errors,
  }
}

// ── Download helper (browser) ─────────────────────────────────

export function downloadNACHAFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=us-ascii" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}